// ================================================================
// 📊 REPORTS API SERVICE - Serviço de API de Relatórios
// ================================================================

import axios from 'axios';
import { API_URL } from '../../config/api';

const API_BASE_URL = API_URL;

/**
 * Configuração de headers com autenticação
 */
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * 📊 ReportsService - Serviço para interagir com a API de Relatórios
 */
class ReportsService {
  /**
   * 📊 Buscar Indicadores (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getIndicators() {
    console.warn('⚠️ [ReportsService] getIndicators() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }

  /**
   * 📊 Buscar Performance de Afiliados (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getAffiliatePerformance() {
    console.warn('⚠️ [ReportsService] getAffiliatePerformance() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }

  /**
   * 📊 Buscar Taxa de Churn (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getChurnRate() {
    console.warn('⚠️ [ReportsService] getChurnRate() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }

  /**
   * 📊 Buscar Abandonos (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getAbandonments() {
    console.warn('⚠️ [ReportsService] getAbandonments() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }

  /**
   * 📊 Buscar Reembolsos (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getRefunds() {
    console.warn('⚠️ [ReportsService] getRefunds() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }

  /**
   * 📊 Buscar Relatório Geral (Compatibilidade com código legado)
   * @deprecated Use getExecutiveDashboard()
   */
  static async getGeneralReport() {
    console.warn('⚠️ [ReportsService] getGeneralReport() está deprecated. Use getExecutiveDashboard()');
    return { success: true, data: {} };
  }
  /**
   * 📊 Buscar Dashboard Executivo
   * @param {Object} filters - Filtros (period, startDate, endDate)
   * @returns {Promise<Object>}
   */
  static async getExecutiveDashboard(filters = {}) {
    try {
      console.log('📊 [ReportsService] Buscando Dashboard Executivo:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.user_type) params.append('user_type', String(filters.user_type));
      if (filters.user_id !== undefined && filters.user_id !== null) params.append('user_id', String(filters.user_id));
      if (filters.product_id !== undefined && filters.product_id !== null) params.append('product_id', String(filters.product_id));
      if (filters.payment_method) params.append('payment_method', String(filters.payment_method));
      if (filters.sale_status) params.append('sale_status', String(filters.sale_status));
      if (filters.region) params.append('region', String(filters.region));

      const queryString = params.toString();
      console.log('📤 [ReportsService] Enviando requisição com filtros:', {
        queryString,
        filtersObject: filters
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/executive-dashboard?${queryString}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Dashboard Executivo retornado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Dashboard Executivo:', error);
      throw error;
    }
  }

  /**
   * 💰 Buscar Relatório de Vendas
   * @param {Object} filters - Filtros (period, startDate, endDate, advancedFilters)
   * @returns {Promise<Object>}
   */
  static async getSalesReport(filters = {}) {
    try {
      console.log('💰 [ReportsService] Buscando Relatório de Vendas:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.advancedFilters?.productId) params.append('productId', filters.advancedFilters.productId);
      if (filters.advancedFilters?.paymentMethod) params.append('paymentMethod', filters.advancedFilters.paymentMethod);
      if (filters.advancedFilters?.saleStatus) params.append('saleStatus', filters.advancedFilters.saleStatus);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/sales?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Vendas retornado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Vendas:', error);
      throw error;
    }
  }

  /**
   * 🤝 Buscar Relatório de Comissões
   * @param {Object} filters - Filtros (period, startDate, endDate, advancedFilters)
   * @returns {Promise<Object>}
   */
  static async getCommissionsReport(filters = {}) {
    try {
      console.log('🤝 [ReportsService] Buscando Relatório de Comissões:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.advancedFilters?.roleCode) params.append('roleCode', filters.advancedFilters.roleCode);
      if (filters.advancedFilters?.commissionStatus) params.append('commissionStatus', filters.advancedFilters.commissionStatus);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/commissions?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Comissões retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Comissões:', error);
      throw error;
    }
  }

  /**
   * 👥 Buscar Relatório de Performance de Afiliados
   * @param {Object} filters - Filtros (period, startDate, endDate)
   * @returns {Promise<Object>}
   */
  static async getAffiliatePerformanceReport(filters = {}) {
    try {
      console.log('👥 [ReportsService] Buscando Relatório de Performance de Afiliados:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/affiliate-performance?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Performance de Afiliados retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Performance de Afiliados:', error);
      throw error;
    }
  }

  /**
   * 📤 Exportar relatório em CSV
   * @param {string} reportType - Tipo do relatório
   * @param {Object} filters - Filtros aplicados
   * @returns {Promise<Blob>}
   */
  static async exportCSV(reportType, filters = {}) {
    try {
      console.log(`📤 [ReportsService] Exportando CSV - Tipo: ${reportType}`, filters);

      const response = await axios.post(
        `${API_BASE_URL}/api/reports/export/csv`,
        {
          report_type: reportType,
          filters
        },
        {
          headers: getHeaders(),
          responseType: 'blob' // Para receber arquivo
        }
      );

      console.log('✅ [ReportsService] CSV exportado com sucesso');
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao exportar CSV:', error);
      throw error;
    }
  }

  /**
   * 💾 Download do arquivo CSV
   * @param {Blob} blob - Blob do arquivo
   * @param {string} filename - Nome do arquivo
   */
  static downloadCSV(blob, filename = 'relatorio.csv') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 📦 Buscar Relatório de Produtos
   * @param {Object} filters - Filtros (period, product_id)
   * @returns {Promise<Object>}
   */
  static async getProductsReport(filters = {}) {
    try {
      console.log('📦 [ReportsService] Buscando Relatório de Produtos:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/products?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Produtos retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Produtos:', error);
      throw error;
    }
  }

  /**
   * 🚚 Buscar Relatório AfterPay / Logística
   * @param {Object} filters - Filtros (period, product_id, sale_status)
   * @returns {Promise<Object>}
   */
  static async getAfterPayReport(filters = {}) {
    try {
      console.log('🚚 [ReportsService] Buscando Relatório AfterPay:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));
      if (filters.sale_status) params.append('sale_status', String(filters.sale_status));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/afterpay?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório AfterPay retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório AfterPay:', error);
      throw error;
    }
  }

  /**
   * 👥 Buscar Relatório de Clientes
   * @param {Object} filters - Filtros (period, user_id)
   * @returns {Promise<Object>}
   */
  static async getClientsReport(filters = {}) {
    try {
      console.log('👥 [ReportsService] Buscando Relatório de Clientes:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.user_id) params.append('user_id', String(filters.user_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/clients?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Clientes retornado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Clientes:', error);
      throw error;
    }
  }

  /**
   * 💰 Buscar Relatório Financeiro
   * @param {Object} filters - Filtros (period, product_id)
   * @returns {Promise<Object>}
   */
  static async getFinancialReport(filters = {}) {
    try {
      console.log('💰 [ReportsService] Buscando Relatório Financeiro:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/financial?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório Financeiro retornado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório Financeiro:', error);
      throw error;
    }
  }

  /**
   * 🎟️ Buscar Relatório de Cupons
   * @param {Object} filters - Filtros (period, product_id)
   * @returns {Promise<Object>}
   */
  static async getCouponsReport(filters = {}) {
    try {
      console.log('🎟️ [ReportsService] Buscando Relatório de Cupons:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/coupons?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Cupons retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Cupons:', error);
      throw error;
    }
  }

  /**
   * 🔄 Buscar Relatório de Churn Rate
   * @param {Object} filters - Filtros (period)
   * @returns {Promise<Object>}
   */
  static async getChurnReport(filters = {}) {
    try {
      console.log('🔄 [ReportsService] Buscando Relatório de Churn Rate:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/churn?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Churn Rate retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Churn Rate:', error);
      throw error;
    }
  }

  /**
   * 🛒 Buscar Relatório de Abandonos
   * @param {Object} filters - Filtros (period, product_id)
   * @returns {Promise<Object>}
   */
  static async getAbandonmentsReport(filters = {}) {
    try {
      console.log('🛒 [ReportsService] Buscando Relatório de Abandonos:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/abandonments?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Abandonos retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Abandonos:', error);
      throw error;
    }
  }

  /**
   * ↩️ Buscar Relatório de Estornos
   * @param {Object} filters - Filtros (period, product_id)
   * @returns {Promise<Object>}
   */
  static async getRefundsReport(filters = {}) {
    try {
      console.log('↩️ [ReportsService] Buscando Relatório de Estornos:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.product_id) params.append('product_id', String(filters.product_id));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/refunds?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Estornos retornado:', response.data);
    return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Estornos:', error);
      throw error;
    }
  }

  /**
   * 👥 Buscar Relatório de Times & Perfis Operacionais
   * @param {Object} filters - Filtros (period)
   * @returns {Promise<Object>}
   */
  static async getTeamsReport(filters = {}) {
    try {
      console.log('👥 [ReportsService] Buscando Relatório de Times:', filters);

      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/teams?${params.toString()}`,
        { headers: getHeaders() }
      );

      console.log('✅ [ReportsService] Relatório de Times retornado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ReportsService] Erro ao buscar Relatório de Times:', error);
      throw error;
    }
  }
}

// Export nomeado para compatibilidade com código legado
export const reportsService = ReportsService;

// Export padrão
export default ReportsService;
